import { useMutation } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { useI18n } from "../i18n/I18nProvider";
import { Button } from "../components/ui/Button";
import { CopyField } from "../components/ui/CopyField";
import { ErrorText } from "../components/ui/feedback";
import { Field } from "../components/ui/Field";
import { Input } from "../components/ui/Input";
import { Modal } from "../components/ui/Modal";
import { inviteLinkFromApiUrl } from "../features/members/queries";
import { adminApi } from "./api";
import type { AdminOperator } from "./types";

export interface OperatorEmailModalProps {
  operator: AdminOperator;
  onClose: () => void;
  onSaved: () => void;
}

/** Changes the sign-in email of one operator. */
export function OperatorEmailModal({ operator, onClose, onSaved }: OperatorEmailModalProps) {
  const { t } = useI18n();
  const [email, setEmail] = useState(operator.email);
  const save = useMutation({
    mutationFn: () => adminApi.updateOperatorEmail(operator.id, email.trim()),
    onSuccess: onSaved,
  });

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (email.trim()) save.mutate();
  };

  return (
    <Modal title={t("admin.operators.changeEmail.title")} onClose={onClose}>
      <form onSubmit={onSubmit} className="space-y-4">
        <Field label={t("admin.operators.newEmail")}>
          {({ id, describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          )}
        </Field>
        <ErrorText error={save.error} />
        <div className="flex justify-end gap-2">
          <Button onClick={onClose} disabled={save.isPending}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" variant="primary" loading={save.isPending}>
            {t("common.save")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export interface SignInLinkModalProps {
  operator: AdminOperator;
  url: string;
  onClose: () => void;
}

/**
 * Shows a one-time sign-in link. Opening it reconnects this operator's existing account; it
 * does not create a new one. The URL lives only in this dialog.
 */
export function SignInLinkModal({ operator, url, onClose }: SignInLinkModalProps) {
  const { t } = useI18n();
  const link = inviteLinkFromApiUrl(url, window.location.origin);
  return (
    <Modal title={t("admin.operators.signIn.title")} onClose={onClose} dismissible={false}>
      <div className="space-y-4">
        <p className="text-note text-ink-2">
          {t("admin.operators.signIn.explain", { email: operator.email })}
        </p>
        <p role="note" className="text-note text-ink-2">
          {t("admin.operators.signIn.shownOnce")}
        </p>
        <CopyField value={link} />
        <div className="flex justify-end">
          <Button variant="primary" onClick={onClose}>
            {t("admin.invite.done")}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
